import { BaseConfiguration } from "./base-configuration";

/**
 * Customer configuration specific to the payment center
 */

export type AccountTypes = { [code: string]: AccountTypeConfiguration };

export interface PaymentTypeConfiguration {
  code: string;
  name: string;
  ordinalPosition: number;
}

export interface PaymentCenterConfiguration extends BaseConfiguration {
  accountTypes: AccountTypes;
  defaultFees: FeesConfiguration;
  quickPayments: QuickPayConfiguration;
  widgets: WidgetConfiguration;
  quickPayTrackingProperties: TrackingProperties[];
  registrationTrackingProperties: TrackingProperties[];
  notifications: NotificationConfiguration;
  statementsEnabled: boolean;
  clientScheduledPayments?: ClientScheduledPaymentsConfiguration;
  applicationName: string | null;
  payByText: PayByTextConfiguration;
}

interface ClientScheduledPaymentsConfiguration {
  maximumScheduledPaymentsAllowed: number;
}

export interface FieldMappingConfiguration {
  displayName: string;
  exportedFieldName: string;
  format: string;
  importedFieldName: string;
  isDisplayed: boolean;
  isReadonly: boolean;
  required: boolean;
}

interface NotificationConfiguration {
  sendNotifications: boolean;
  sendWelcomeEmail: boolean;
  sendEStatementEnrollment: boolean;
  sendPaymentReminder: boolean;
  sendRecurringPaymentReminder: boolean;
  sendPastDue: boolean;
  daysBeforeDueDateToSendPaymentDueReminder: number;
  daysBeforeDueDateToSendPaymentScheduledReminder: number;
  daysAfterDueDateToSendPastDue: number;
}

export interface AccountDetailsConfiguration {
  displayName: string;
  pastDueAfter: string;
  mappings: string[];
}

export interface AccountTypeConfiguration {
  name: string;
  accountDetails: AccountDetailsConfiguration;
  paymentMethods: { [key: string]: PaymentMethodConfiguration };
  fieldMappings: { [key: string]: FieldMappingConfiguration };
  paymentTypes: PaymentTypeConfiguration[];
  quickPayments: QuickPayAccountTypeConfiguration;
  associatedRealTimeHandler?: string;
}

interface FeesConfiguration {
  convenienceFee?: number;
}

export interface FeeConfiguration {
  name: string;
  description?: string;
  amount: number;
  isPercentage: boolean;
  typeCode?: number;
  isRecurring?: boolean;
}

export interface PayByTextConfiguration {
  enabled: boolean;
}

interface PaymentConfiguration {
  minimumPaymentAmount?: number;
  maximumPaymentAmount?: number;
  overlimitAmount?: number;
  amountSelectionOption: string[];
  newKeyForTest_AccountTransLimit?: number;
}

export interface PaymentMethodConfiguration {
  name: string;
  enabled: boolean;
  payments: PaymentConfiguration;
  fees: { ConvenienceFee: FeeConfiguration };
  scheduledPayments: ScheduledPaymentsConfiguration;
  id: number;
  merchantId: string;
  walletProviders?: WalletProviderConfiguration[];
}

/* interface LockboxPaymentMethodConfiguration extends PaymentMethodConfiguration {
  mailingAddressLine1: string;
  mailingAddressLine2?: string;
  mailingAddressCity: string;
  mailingAddressState: string;
  mailingAddressPostalCode: string;
  mailingAddressCountry: string;
}

interface PaymentMethodsConfiguration {
  ach: PaymentMethodConfiguration;
  card: PaymentMethodConfiguration;
  ewallet: PaymentMethodConfiguration;
  lockbox: LockboxPaymentMethodConfiguration;
} */

export enum QuickPayCompletionLinkType {
  None = 0,
  Close = 1,
  Navigate = 2,
  NavigatePost = 3,
}

export enum QuickPayType {
  Validated = 1,
  NonValidated = 2,
}

interface QuickPayConfiguration {
  enabled: boolean;
  captchaEnabled: boolean;
  deepLinkingOnly: boolean;
  quickPayType: QuickPayType;
  customerDetails: QuickPayCustomerDetailsConfiguration;
}

interface QuickPayCustomerDetailsConfiguration {
  cellPhone: boolean;
  note: boolean;
  companyName: boolean;
}

interface QuickPayAccountTypeConfiguration {
  completionPageLinkFunctionality: QuickPayCompletionLinkType;
  postCompletionLinkUrl?: string;
}

interface ScheduledPaymentsConfiguration {
  enabled: boolean;
  dateOptions: string[];
}

export interface WalletProviderConfiguration {
  enabled: boolean;
  walletType: number;
}

interface WidgetConfiguration {
  editingEnabled: boolean;
}

export interface TrackingProperties {
  displayName: string;
  isCustomProperty: boolean;
  isInvoiceProperty: boolean;
  propertyName: string;
  providedForValidation: boolean;
  value?: string;
}
